import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Users,
  Clock,
  CheckCircle,
  Zap,
  Palette,
  Code,
  BarChart3,
  MessageSquare,
  Shield,
  Award,
  Play,
  FileImage,
  Layers,
  RefreshCw
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useCMSContent } from '@/hooks/use-cms-content';
import { fetchPlatformPage, type PlatformPageData } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const services = [
  {
    icon: Palette,
    title: 'Creative Design',
    description: 'Our expert designers create stunning, on-brand interactive ad creatives that capture attention and drive engagement.',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    icon: Code,
    title: 'Development',
    description: 'Professional HTML5 development with clean, optimized code that works flawlessly across all ad servers and platforms.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: BarChart3,
    title: 'Campaign Optimization',
    description: 'Data-driven optimization to continuously improve performance and maximize your advertising ROI.',
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    icon: MessageSquare,
    title: 'Dedicated Support',
    description: 'Direct communication with your creative team throughout the entire process, from brief to delivery.',
    gradient: 'from-amber-500 to-orange-500',
  },
];

const adFormats = [
  { name: 'Rich Media Banners', description: 'Interactive HTML5 banners with animations and engagement features' },
  { name: 'Video Ads', description: 'In-stream and outstream video with interactive overlays' },
  { name: 'Native Ads', description: 'Content-style placements that blend seamlessly with editorial' },
  { name: 'Expandable Units', description: 'High-impact formats that expand on user interaction' },
  { name: 'Interstitials', description: 'Full-screen takeover experiences for maximum impact' },
  { name: 'Carousel Ads', description: 'Multi-slide creative for showcasing multiple products or messages' },
];

const process = [
  {
    step: 1,
    title: 'Submit Brief',
    description: 'Share your campaign goals, brand assets, and creative direction through our simple request form.',
    icon: FileImage,
  },
  {
    step: 2,
    title: 'Design & Develop',
    description: 'Our team creates your interactive ad units with professional design and clean code.',
    icon: Layers,
  },
  {
    step: 3,
    title: 'Review & Refine',
    description: 'Preview your ads, provide feedback, and request unlimited revisions until perfect.',
    icon: RefreshCw,
  },
  {
    step: 4,
    title: 'Launch',
    description: 'Receive your finished ad units ready to traffic across any ad server.',
    icon: Zap,
  },
];

const stats = [
  { value: '$500', label: 'Per ad unit' },
  { value: '5-7', label: 'Business days' },
  { value: '100%', label: 'Satisfaction rate' },
  { value: '40+', label: 'Ad servers supported' },
];

const ManagedServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const fetchFn = useCallback(() => fetchPlatformPage('managed-services'), []);
  const { data: cms } = useCMSContent<PlatformPageData | null>('platform-managed-services', fetchFn, null);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Managed Services' },
      ]} />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 lg:pt-0 overflow-hidden">
        {/* Stripe-style gradient background */}
        <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
          {/* Grid pattern */}
          <div className="absolute inset-0 grid-pattern" />
          
          {/* Animated gradient orbs */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] opacity-30 dark:opacity-40">
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-transparent rounded-full blur-3xl animate-float" />
          </div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] opacity-20 dark:opacity-30">
            <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-tr from-amber-500 via-[#635bff] to-transparent rounded-full blur-3xl animate-float-delayed" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="max-w-xl">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 border border-[#635bff]/20 dark:border-[#635bff]/30 text-sm font-medium text-slate-700 dark:text-slate-200 mb-8">
                <Briefcase className="w-4 h-4 mr-2 text-[#635bff]" />
                Done-For-You Ad Creation
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.05]">
                <span className="text-slate-900 dark:text-white">{cms?.heroHeadline || 'Let experts build your '}</span>
                <span className="stripe-gradient-text">{cms?.heroHeadlineGradient || 'interactive ads.'}</span>
              </h1>

              <p className="mt-8 text-lg lg:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
                {cms?.heroSubheadline || 'Focus on strategy while our professional creative team handles design, development, and optimization. Get high-performance ad units delivered fast.'}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/demo')}
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-slate-700 dark:text-slate-200 bg-white/80 dark:bg-white/10 backdrop-blur-sm border border-slate-200 dark:border-white/20 rounded-full hover:bg-white dark:hover:bg-white/20 hover:border-slate-300 dark:hover:border-white/30 transition-all group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  See Examples
                </button>
              </div>
            </div>

            {/* Right Content - Service Preview */}
            <div className="relative lg:pl-8">
              {/* Glow effect behind card */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#635bff]/20 via-[#7c3aed]/20 to-amber-500/20 rounded-3xl blur-2xl opacity-60 dark:opacity-40" />
              
              {/* Main Preview */}
              <div className="relative bg-white dark:bg-slate-800/50 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 overflow-hidden border border-slate-200/50 dark:border-white/10 backdrop-blur-xl p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#635bff] to-[#7c3aed] rounded-xl flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">Managed Services</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Professional ad creation</p>
                  </div>
                </div>

                {/* Mock Service UI */}
                <div className="space-y-4">
                  {/* Pricing */}
                  <div className="p-4 bg-gradient-to-r from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Per Ad Unit</p>
                      <span className="text-2xl font-bold text-[#635bff]">$500</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Includes design, development, and QA</p>
                  </div>

                  {/* Delivery */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                      <Clock className="w-5 h-5 text-slate-400 mb-2" />
                      <p className="text-sm font-medium text-slate-900 dark:text-white">5-7 Days</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Standard delivery</p>
                    </div>
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                      <Zap className="w-5 h-5 text-amber-500 mb-2" />
                      <p className="text-sm font-medium text-slate-900 dark:text-white">2-3 Days</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Rush (+$250/unit)</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2">
                    {['Unlimited Revisions', 'All Ad Servers', 'QA Tested'].map((feature) => (
                      <span key={feature} className="flex items-center gap-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Stats Cards */}
              {/* <div className="absolute -left-8 top-1/4 glass-card rounded-xl shadow-xl p-4 hidden lg:block animate-float">
                <div className="text-2xl font-bold stripe-gradient-text">100%</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Satisfaction</div>
              </div>
              <div className="absolute -right-8 bottom-1/4 glass-card rounded-xl shadow-xl p-4 hidden lg:block animate-float-delayed">
                <div className="text-2xl font-bold text-emerald-500">40+</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Ad Servers</div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900 dark:bg-slate-950">
          <div className="absolute inset-0 grid-pattern opacity-30" />
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-[#635bff] to-[#7c3aed] rounded-full blur-3xl" />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold stripe-gradient-text">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-slate-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Services Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
          <div className="absolute inset-0 grid-pattern" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
              Full-service{' '}
              <span className="stripe-gradient-text">
                creative support
              </span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
              Our expert team handles every aspect of interactive ad creation so you can focus on strategy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group relative bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-slate-200/50 dark:border-white/10 hover:bg-white dark:hover:bg-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all duration-300 stripe-card"
              >
                {/* Icon with gradient background */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} mb-6 shadow-lg`}>
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{service.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{service.description}</p>
                
                {/* Hover glow effect */}
                <div className={`absolute -inset-px bg-gradient-to-r ${service.gradient} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Formats Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-50/50 dark:bg-white/[0.02]">
          <div className="absolute inset-0 grid-pattern" />
          <div className="absolute right-0 top-0 w-[600px] h-[600px] opacity-20 dark:opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-[#635bff] to-[#7c3aed] rounded-full blur-3xl" />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
              Every format,{' '}
              <span className="stripe-gradient-text">
                expertly crafted
              </span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
              From rich media banners to full-screen interstitials, we build any interactive ad format you need.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {adFormats.map((format, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800/50 rounded-xl p-6 border border-slate-200/50 dark:border-white/10 hover:shadow-lg hover:border-[#635bff] dark:hover:border-[#635bff] transition-all cursor-pointer group"
              >
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-[#635bff] transition-colors">{format.name}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{format.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
          <div className="absolute inset-0 grid-pattern" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
              Simple{' '}
              <span className="stripe-gradient-text">
                4-step process
              </span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
              From brief to launch in days, not weeks. Here's how it works.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector line */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-[#635bff] to-[#7c3aed] opacity-20" />
                )}
                
                <div className="relative bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-slate-200/50 dark:border-white/10 text-center h-full">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#635bff] to-[#7c3aed] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 bg-white dark:bg-slate-900 border-2 border-[#635bff] rounded-full flex items-center justify-center text-[#635bff] font-bold text-sm">
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2 mt-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-[#00d4ff]" />
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-white/20 to-transparent rounded-full blur-3xl animate-float" />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight">
            {cms?.ctaHeadline || 'Ready to get started?'}
          </h2>
          <p className="mt-6 text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
            {cms?.ctaSubheadline || 'Submit your first request today and get professional interactive ads delivered in days.'}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/demo')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/20 hover:border-white/30 transition-all"
            >
              <Play className="mr-2 h-5 w-5" />
              See Examples
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ManagedServicesPage;
